import React, { useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import { Button } from '../../../components/UI/Button';

import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';

import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';

import {

  FlowComposerFlowCanvas,

  type FlowComposerFlowCanvasHandle,

} from '../../../components/AgentPlatform/FlowComposerFlowCanvas';

import { FlowComposerPalette } from '../../../components/AgentPlatform/FlowComposerPalette';

import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';

import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';

import { JsonTextArea } from '../../../components/AgentPlatform/JsonTextArea';

import { useToast } from '../../../components/UI/ToastProvider';

import { agentPlatformService } from '../../../services/agentPlatformService';

import type { AgentFlowDto, AutomationInstanceDto } from '../../../types/agentPlatform';

import { apTheme } from '../../../utils/agentPlatformTheme';



type ComposerStep = {

  id: string;

  label: string;

  nodeType: 'inject' | 'function' | 'http response';

};



type PanelId = 'canvas' | 'visual' | 'json' | 'properties' | 'advanced';



const PANELS: { id: PanelId; label: string }[] = [

  { id: 'canvas', label: 'Canvas' },

  { id: 'visual', label: 'Pipeline' },

  { id: 'json', label: 'JSON' },

  { id: 'properties', label: 'Propriedades' },

  { id: 'advanced', label: 'Avançado' },

];



export const FlowComposerPage: React.FC = () => {

  const { id } = useParams<{ id: string }>();

  const { addToast } = useToast();

  const canvasRef = useRef<FlowComposerFlowCanvasHandle>(null);

  const [flow, setFlow] = useState<AgentFlowDto | null>(null);

  const [instance, setInstance] = useState<AutomationInstanceDto | null>(null);

  const [definition, setDefinition] = useState('[]');

  const [steps, setSteps] = useState<ComposerStep[]>([

    { id: '1', label: 'Know Flow', nodeType: 'inject' },

  ]);

  const [activePanel, setActivePanel] = useState<PanelId>('canvas');

  const [saving, setSaving] = useState(false);

  const [instances, setInstances] = useState<AutomationInstanceDto[]>([]);

  const [meta, setMeta] = useState({ name: '', description: '', automationInstanceId: '' });

  const [inputSchema, setInputSchema] = useState('{\n  "type": "object"\n}');

  const [outputSchema, setOutputSchema] = useState('{\n  "type": "object"\n}');

  const [callbackConfiguration, setCallbackConfiguration] = useState(

    '{\n  "triggerPath": "/know/automation/flow",\n  "triggerMethod": "POST"\n}'

  );

  const [changeNotes, setChangeNotes] = useState('');



  useEffect(() => {

    if (!id) return;

    const flowId = id;

    async function load() {

      try {

        const f = await agentPlatformService.getFlow(flowId);

        setFlow(f);

        setMeta({

          name: f.name,

          description: f.description ?? '',

          automationInstanceId: f.automationInstanceId,

        });

        const instList = await agentPlatformService.getAutomationInstances();

        setInstances(instList);

        const inst = instList.find(i => i.id === f.automationInstanceId) ?? null;

        setInstance(inst);

        const version = f.currentVersionDetail ?? f.publishedVersionDetail;
        if (version) {
          setDefinition(version.definition);
          if (version.inputSchema) setInputSchema(version.inputSchema);
          if (version.outputSchema) setOutputSchema(version.outputSchema);
          if (version.callbackConfiguration) {
            setCallbackConfiguration(version.callbackConfiguration);
          }
        }

      } catch {

        addToast('Fluxo não encontrado.', 'error');

      }

    }

    load();

  }, [id, addToast]);



  const buildDefinitionFromSteps = (): string => {

    const tabId = `know-${flow?.code ?? 'flow'}`;

    const nodes = steps.map((step, index) => ({

      id: `${tabId}-node-${index}`,

      type: step.nodeType === 'inject' ? 'inject' : step.nodeType === 'function' ? 'function' : 'http response',

      z: tabId,

      name: step.label,

      x: 120 + index * 180,

      y: 120,

      wires: index < steps.length - 1 ? [[`${tabId}-node-${index + 1}`]] : [],

    }));



    const tab = {

      id: tabId,

      type: 'tab',

      label: steps[0]?.label ?? 'Know Flow',

      disabled: false,

    };



    return JSON.stringify([tab, ...nodes], null, 2);

  };



  const syncVisualToJson = () => {

    setDefinition(buildDefinitionFromSteps());

    addToast('JSON gerado a partir dos passos.', 'success');

  };



  const saveVersion = async () => {

    if (!id) return;

    setSaving(true);

    try {

      const payload =

        activePanel === 'visual' || activePanel === 'canvas'

          ? definition || buildDefinitionFromSteps()

          : definition;

      const updated = await agentPlatformService.createFlowVersion(id, {

        definition: payload,

        inputSchema,

        outputSchema,

        callbackConfiguration,

        changeNotes: changeNotes.trim() || 'Editor visual',

      });

      setFlow(updated);

      setDefinition(payload);

      addToast(`Versão v${updated.currentVersionNumber} salva.`, 'success');

    } catch {

      addToast('Erro ao salvar versão.', 'error');

    } finally {

      setSaving(false);

    }

  };



  const saveMetadata = async () => {

    if (!id) return;

    try {

      const updated = await agentPlatformService.updateFlow(id, {

        name: meta.name.trim(),

        description: meta.description.trim() || undefined,

        automationInstanceId: meta.automationInstanceId,

      });

      setFlow(updated);

      const inst = instances.find(i => i.id === updated.automationInstanceId) ?? null;

      setInstance(inst);

      addToast('Metadados atualizados.', 'success');

    } catch {

      addToast('Erro ao atualizar metadados.', 'error');

    }

  };



  const publish = async () => {

    if (!id) return;

    try {

      const updated = await agentPlatformService.publishFlow(id);

      setFlow(updated);

      addToast('Fluxo publicado na instância de automação.', 'success');

    } catch {

      addToast('Erro ao publicar.', 'error');

    }

  };



  if (!flow) {

    return (

      <div className="min-h-[50vh] flex items-center justify-center bg-[#363130] text-slate-400">

        Carregando composer…

      </div>

    );

  }



  return (

    <AgentPlatformFeatureGate feature="flows">

      <AgentPlatformAdminGate>

        <AgentPlatformShell variant="composer" contentClassName="max-w-none px-0 py-0 flex flex-col flex-1 min-h-0">

          <div className={apTheme.composerToolbar}>

            <AgentPlatformPageHeader

              backTo="/agent-platform/flows"

              backLabel="Fluxos"

              title={flow.name}

              subtitle={`${flow.code} · v${flow.currentVersionNumber ?? 1}${flow.isPublished ? ' · publicado' : ''}`}

              area="workflow"

              tone="dark"

              className="flex-1"

              actions={

                <>

                  <Button variant="secondary" onClick={saveVersion} disabled={saving}>

                    Salvar versão

                  </Button>

                  <Button onClick={publish}>Publicar / Deploy</Button>

                </>

              }

            />

          </div>



          <div className="flex flex-wrap gap-1 px-4 border-b border-white/10 bg-[#242120]">

            {PANELS.map(panel => (

              <button

                key={panel.id}

                type="button"

                onClick={() => setActivePanel(panel.id)}

                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${

                  activePanel === panel.id

                    ? 'bg-[#363130] text-red-300 border border-white/10 border-b-transparent -mb-px'

                    : 'text-slate-400 hover:text-slate-200'

                }`}

              >

                {panel.label}

              </button>

            ))}

          </div>



          {activePanel === 'canvas' && (

            <div className="flex flex-1 min-h-[calc(100vh-12rem)]">

              <FlowComposerPalette

                onAddInject={() => canvasRef.current?.addNode('inject', 'Gatilho')}

                onAddFunction={() => canvasRef.current?.addNode('function', 'Função')}

                onAddHttpResponse={() => canvasRef.current?.addNode('http response', 'Resposta HTTP')}

              />

              <FlowComposerFlowCanvas

                ref={canvasRef}

                definitionJson={definition}

                flowCode={flow.code}

                onDefinitionChange={setDefinition}

                className="flex-1 min-h-0"

              />

            </div>

          )}



          {activePanel === 'visual' && (

            <div className="flex-1 overflow-auto p-6 bg-[#242120]">

              <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-6">

                <div className="lg:col-span-2 rounded-xl border border-white/10 bg-[#242120] p-6 min-h-[320px]">

                  <h2 className="text-sm font-semibold text-slate-300 mb-4">Pipeline simplificado</h2>

                  <div className="flex flex-wrap items-center gap-3">

                    {steps.map((step, index) => (

                      <React.Fragment key={step.id}>

                        <div className="border border-red-500/40 bg-red-500/10 rounded-xl px-4 py-3 min-w-[140px]">

                          <input

                            className="font-medium text-sm bg-transparent border-b border-red-400/30 w-full mb-1 text-white"

                            value={step.label}

                            onChange={e => {

                              const next = [...steps];

                              next[index] = { ...step, label: e.target.value };

                              setSteps(next);

                            }}

                          />

                          <select

                            className="text-xs w-full border border-white/10 rounded bg-[#363130] text-slate-300 px-1 py-0.5"

                            value={step.nodeType}

                            onChange={e => {

                              const next = [...steps];

                              next[index] = {

                                ...step,

                                nodeType: e.target.value as ComposerStep['nodeType'],

                              };

                              setSteps(next);

                            }}

                          >

                            <option value="inject">Gatilho (inject)</option>

                            <option value="function">Função</option>

                            <option value="http response">Resposta HTTP</option>

                          </select>

                        </div>

                        {index < steps.length - 1 && (

                          <span className="text-slate-500 text-xl">→</span>

                        )}

                      </React.Fragment>

                    ))}

                  </div>

                  <div className="mt-6 flex gap-2">

                    <Button

                      variant="secondary"

                      size="sm"

                      onClick={() =>

                        setSteps(s => [

                          ...s,

                          {

                            id: String(Date.now()),

                            label: `Passo ${s.length + 1}`,

                            nodeType: 'function',

                          },

                        ])

                      }

                    >

                      + Passo

                    </Button>

                    <Button variant="secondary" size="sm" onClick={syncVisualToJson}>

                      Atualizar JSON

                    </Button>

                  </div>

                </div>

                <div className="rounded-xl border border-white/10 bg-[#242120] p-4">

                  <h3 className="text-sm font-semibold text-slate-300 mb-2">Preview JSON</h3>

                  <pre className="text-xs font-mono text-slate-400 bg-black/20 p-3 rounded-lg overflow-auto max-h-64">

                    {buildDefinitionFromSteps()}

                  </pre>

                </div>

              </div>

            </div>

          )}



          {activePanel === 'json' && (

            <div className="flex-1 overflow-auto p-6 bg-[#242120]">

              <div className="max-w-4xl mx-auto rounded-xl border border-white/10 bg-[#242120] p-6">

                <JsonTextArea
                  label="Definição do fluxo (array de nós)"
                  value={definition}
                  onChange={setDefinition}
                  rows={22}
                  variant="composer"
                />

              </div>

            </div>

          )}



          {activePanel === 'properties' && (

            <div className="flex-1 overflow-auto p-6 bg-[#242120]">

              <div className="max-w-2xl mx-auto rounded-xl border border-white/10 bg-[#242120] p-6 space-y-4">

                <div>

                  <label className="block text-sm font-medium text-slate-300 mb-1">Nome</label>

                  <input

                    className="w-full border border-white/10 rounded-lg px-3 py-2 bg-[#363130] text-slate-200"

                    value={meta.name}

                    onChange={e => setMeta(m => ({ ...m, name: e.target.value }))}

                  />

                </div>

                <div>

                  <label className="block text-sm font-medium text-slate-300 mb-1">Descrição</label>

                  <textarea

                    className="w-full border border-white/10 rounded-lg px-3 py-2 bg-[#363130] text-slate-200"

                    rows={2}

                    value={meta.description}

                    onChange={e => setMeta(m => ({ ...m, description: e.target.value }))}

                  />

                </div>

                <div>

                  <label className="block text-sm font-medium text-slate-300 mb-1">Instância de Automação</label>

                  <select

                    className="w-full border border-white/10 rounded-lg px-3 py-2 bg-[#363130] text-slate-200"

                    value={meta.automationInstanceId}

                    onChange={e => setMeta(m => ({ ...m, automationInstanceId: e.target.value }))}

                  >

                    {instances.map(i => (

                      <option key={i.id} value={i.id}>

                        {i.name} — {i.baseUrl}

                      </option>

                    ))}

                  </select>

                </div>

                <Button variant="secondary" onClick={saveMetadata}>

                  Salvar metadados

                </Button>

                <hr className="border-white/10" />

                <JsonTextArea label="Input schema" value={inputSchema} onChange={setInputSchema} rows={5} variant="composer" />

                <JsonTextArea label="Output schema" value={outputSchema} onChange={setOutputSchema} rows={5} variant="composer" />

                <JsonTextArea
                  label="Callback configuration"
                  value={callbackConfiguration}
                  onChange={setCallbackConfiguration}
                  rows={5}
                  variant="composer"
                />

                <div>

                  <label className="block text-sm font-medium text-slate-300 mb-1">Notas da próxima versão</label>

                  <input

                    className="w-full border border-white/10 rounded-lg px-3 py-2 text-sm bg-[#363130] text-slate-200"

                    value={changeNotes}

                    onChange={e => setChangeNotes(e.target.value)}

                    placeholder="Salvo junto com a definição"

                  />

                </div>

              </div>

            </div>

          )}



          {activePanel === 'advanced' && (

            <div className="flex-1 overflow-auto p-6 bg-[#242120]">

              <div className="max-w-2xl mx-auto rounded-xl border border-white/10 bg-[#242120] p-6">

                <h2 className="text-lg font-semibold text-white mb-2">Console da instância</h2>

                <p className="text-sm text-slate-400 mb-4">

                  Acesso direto ao runtime de automação para ajustes finos. A plataforma permanece como

                  fonte da verdade — alterações manuais podem ser sobrescritas no próximo deploy.

                </p>

                {instance ? (

                  <a

                    href={instance.baseUrl}

                    target="_blank"

                    rel="noopener noreferrer"

                    className="text-red-300 font-medium hover:text-red-200 transition-colors"

                  >

                    Abrir {instance.name} →

                  </a>

                ) : (

                  <p className="text-sm text-amber-400">Instância de automação não encontrada.</p>

                )}

              </div>

            </div>

          )}

        </AgentPlatformShell>

      </AgentPlatformAdminGate>

    </AgentPlatformFeatureGate>

  );

};


